import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Hives from "@/pages/Hives";
import HiveDetail from "@/pages/HiveDetail";
import Events from "@/pages/Events";
import Harvests from "@/pages/Harvests";
import Diagnose from "@/pages/Diagnose";
import CompareHives from "@/pages/CompareHives";
import SmartCalendar from "@/pages/SmartCalendar";
import WordGame from "@/pages/WordGame";
import GamesHub from "@/pages/GamesHub";
import BeeSweeper from "@/pages/BeeSweeper";
import HoneySort from "@/pages/HoneySort";
import BeeMatch from "@/pages/BeeMatch";
import HoneyTycoon from "@/pages/HoneyTycoon";
import EscapeBees from "@/pages/EscapeBees";
import NectarTrail from "@/pages/NectarTrail";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import GettingStarted from "@/pages/GettingStarted";
import Guide from "@/pages/Guide";
import GuideArticle from "@/pages/GuideArticle";
import PublicShare from "@/pages/PublicShare";
import PublicPage from "@/pages/PublicPage";
import InviteAccept from "@/pages/InviteAccept";
import Forum, { ForumCategory, ForumThread, ForumNewThread } from "@/pages/Forum";
import PublicSupportReply from "@/pages/PublicSupportReply";
import Policy from "@/pages/Policy";
import CookiePolicy from "@/pages/CookiePolicy";
import FaqPage from "@/pages/FaqPage";
import CookieConsentBanner from "@/components/CookieConsentBanner";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/share/:slug" element={<PublicShare />} />
            <Route path="/p/:slug" element={<PublicPage />} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/forum/c/:slug" element={<ForumCategory />} />
            <Route path="/forum/t/:slug" element={<ForumThread />} />
            <Route path="/forum/new" element={<ForumNewThread />} />
            <Route path="/invites/:token" element={<InviteAccept />} />
            <Route path="/support/reply/:token" element={<PublicSupportReply />} />
            <Route path="/policy" element={<Policy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="hives" element={<Hives />} />
              <Route path="hives/:id" element={<HiveDetail />} />
              <Route path="events" element={<Events />} />
              <Route path="harvests" element={<Harvests />} />
              <Route path="diagnose" element={<Diagnose />} />
              <Route path="compare" element={<CompareHives />} />
              <Route path="smart-calendar" element={<SmartCalendar />} />
              <Route path="games" element={<GamesHub />} />
              <Route path="games/word" element={<WordGame />} />
              <Route path="games/beesweeper" element={<BeeSweeper />} />
              <Route path="games/honeysort" element={<HoneySort />} />
              <Route path="games/beematch" element={<BeeMatch />} />
              <Route path="games/honeytycoon" element={<HoneyTycoon />} />
              <Route path="games/escapebees" element={<EscapeBees />} />
              <Route path="games/nectartrail" element={<NectarTrail />} />
              <Route path="word-game" element={<WordGame />} />
              <Route path="admin" element={<Admin />} />
              <Route path="settings" element={<Settings />} />
              <Route path="getting-started" element={<GettingStarted />} />
              <Route path="guide" element={<Guide />} />
              <Route path="guide/:slug" element={<GuideArticle />} />
            </Route>
          </Routes>
          <CookieConsentBanner />
        </BrowserRouter>
        <Toaster richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
